/* ─────────────────────────────────────────────────────────────
   Every destination on the dashboard, for both of its audiences.

   `screen` is the key Screen switches on and the rail marks active;
   `label` is only what the reader sees. The original six keep the keys
   they have always had (Spa's label and key still differ).

   Residents see the hospitality modules and their own half of the
   shared ones. Staff see operations, security and administration, with
   the property dashboard first. Both lists follow the module menu in
   the pricing deck, so a demo can walk it in order.
   ───────────────────────────────────────────────────────────── */

import ValetScreen from './valet-screen.js';
import PackagesScreen from './packages-screen.js';
import NewsScreen from './news-screen.js';
import RestaurantScreen from './restaurant-screen.js';
import SpaScreen from './spa-screen.js';
import PoolScreen from './pool-screen.js';

import ReservationsScreen from './resident/reservations-screen.js';
import ConciergeScreen from './resident/concierge-screen.js';
import EventsScreen from './resident/events-screen.js';
import ServiceRequestsScreen from './resident/service-requests-screen.js';
import GuestsScreen from './resident/guests-screen.js';
import PetsScreen from './resident/pets-screen.js';
import VehiclesScreen from './resident/vehicles-screen.js';
import MoveScreen from './resident/move-screen.js';
import FormsScreen from './resident/forms-screen.js';
import DocumentsScreen from './resident/documents-screen.js';
import BillingScreen from './resident/billing-screen.js';
import LiveCamsScreen from './resident/live-cams-screen.js';
import DoorAccessScreen from './resident/door-access-screen.js';

import OverviewScreen from './staff/overview-screen.js';
import WorkOrdersScreen from './staff/work-orders-screen.js';
import MaintenanceScreen from './staff/maintenance-screen.js';
import HousekeepingScreen from './staff/housekeeping-screen.js';
import TasksScreen from './staff/tasks-screen.js';
import VendorsScreen from './staff/vendors-screen.js';
import InventoryScreen from './staff/inventory-screen.js';
import InspectionsScreen from './staff/inspections-screen.js';
import DeliveriesScreen from './staff/deliveries-screen.js';
import VisitorsScreen from './staff/visitors-screen.js';
import KeysScreen from './staff/keys-screen.js';
import ParkingScreen from './staff/parking-screen.js';
import IncidentsScreen from './staff/incidents-screen.js';
import EmergencyScreen from './staff/emergency-screen.js';
import ApprovalsScreen from './staff/approvals-screen.js';
import AnalyticsScreen from './staff/analytics-screen.js';
import CamerasScreen from './staff/cameras-screen.js';
import AccessControlScreen from './staff/access-control-screen.js';

import { ReactComponent as Valet } from '../media/images/svg/valet.svg';
import { ReactComponent as Package } from '../media/images/svg/package.svg';
import { ReactComponent as News } from '../media/images/svg/news.svg';
import { ReactComponent as Restaurant } from '../media/images/svg/restaurant.svg';
import { ReactComponent as Spa } from '../media/images/svg/spa.svg';
import { ReactComponent as Pool } from '../media/images/svg/pool.svg';
import { ReactComponent as Calendar } from '../media/images/svg/event_available.svg';
import { ReactComponent as Bell } from '../media/images/svg/room_service.svg';
import { ReactComponent as Party } from '../media/images/svg/celebration.svg';
import { ReactComponent as Wrench } from '../media/images/svg/build.svg';
import { ReactComponent as PersonAdd } from '../media/images/svg/person_add.svg';
import { ReactComponent as Paw } from '../media/images/svg/pets.svg';
import { ReactComponent as Car } from '../media/images/svg/directions_car.svg';
import { ReactComponent as Truck } from '../media/images/svg/local_shipping.svg';
import { ReactComponent as Form } from '../media/images/svg/edit_document.svg';
import { ReactComponent as Folder } from '../media/images/svg/folder_open.svg';
import { ReactComponent as Receipt } from '../media/images/svg/receipt_long.svg';
import { ReactComponent as Dashboard } from '../media/images/svg/dashboard.svg';
import { ReactComponent as Handyman } from '../media/images/svg/handyman.svg';
import { ReactComponent as Engineering } from '../media/images/svg/engineering.svg';
import { ReactComponent as Cleaning } from '../media/images/svg/cleaning_services.svg';
import { ReactComponent as Task } from '../media/images/svg/task_alt.svg';
import { ReactComponent as Store } from '../media/images/svg/storefront.svg';
import { ReactComponent as Boxes } from '../media/images/svg/inventory_2.svg';
import { ReactComponent as Checklist } from '../media/images/svg/fact_check.svg';
import { ReactComponent as Forklift } from '../media/images/svg/forklift.svg';
import { ReactComponent as Badge } from '../media/images/svg/badge.svg';
import { ReactComponent as Key } from '../media/images/svg/key.svg';
import { ReactComponent as Parking } from '../media/images/svg/local_parking.svg';
import { ReactComponent as Report } from '../media/images/svg/report.svg';
import { ReactComponent as Megaphone } from '../media/images/svg/campaign.svg';
import { ReactComponent as Approval } from '../media/images/svg/approval.svg';
import { ReactComponent as Chart } from '../media/images/svg/monitoring.svg';
import { ReactComponent as Videocam } from '../media/images/svg/videocam.svg';
import { ReactComponent as Door } from '../media/images/svg/door_front.svg';

export const RESIDENT_SCREENS = [
    { screen: 'Valet',            label: 'Valet',            Icon: Valet,      Component: ValetScreen },
    { screen: 'Packages',         label: 'Packages',         Icon: Package,    Component: PackagesScreen },
    { screen: 'Restaurant',       label: 'Restaurant',       Icon: Restaurant, Component: RestaurantScreen },
    { screen: 'Spa Scheduling',   label: 'Spa Schedule',     Icon: Spa,        Component: SpaScreen },
    { screen: 'Pool / Beach',     label: 'Pool / Beach',     Icon: Pool,       Component: PoolScreen },
    { screen: 'Live Cams',        label: 'Live Cams',        Icon: Videocam,   Component: LiveCamsScreen },
    { screen: 'Reservations',     label: 'Reservations',     Icon: Calendar,   Component: ReservationsScreen },
    { screen: 'Concierge',        label: 'Concierge',        Icon: Bell,       Component: ConciergeScreen },
    { screen: 'Events',           label: 'Events',           Icon: Party,      Component: EventsScreen },
    { screen: 'Building News',    label: 'Building News',    Icon: News,       Component: NewsScreen },
    { screen: 'Service Requests', label: 'Service Requests', Icon: Wrench,     Component: ServiceRequestsScreen },
    { screen: 'Door Access',      label: 'Door Access',      Icon: Door,       Component: DoorAccessScreen },
    { screen: 'Guests',           label: 'Guests',           Icon: PersonAdd,  Component: GuestsScreen },
    { screen: 'Pets',             label: 'Pets',             Icon: Paw,        Component: PetsScreen },
    { screen: 'Vehicles',         label: 'Vehicles',         Icon: Car,        Component: VehiclesScreen },
    { screen: 'Move In / Out',    label: 'Move In / Out',    Icon: Truck,      Component: MoveScreen },
    { screen: 'Forms',            label: 'Forms',            Icon: Form,       Component: FormsScreen },
    { screen: 'Documents',        label: 'Documents',        Icon: Folder,     Component: DocumentsScreen },
    { screen: 'Billing',          label: 'Billing',          Icon: Receipt,    Component: BillingScreen }
];

export const STAFF_SCREENS = [
    { screen: 'Overview',      label: 'Overview',      Icon: Dashboard,   Component: OverviewScreen },
    { screen: 'Work Orders',   label: 'Work Orders',   Icon: Handyman,    Component: WorkOrdersScreen },
    { screen: 'Maintenance',   label: 'Maintenance',   Icon: Engineering, Component: MaintenanceScreen },
    { screen: 'Housekeeping',  label: 'Housekeeping',  Icon: Cleaning,    Component: HousekeepingScreen },
    { screen: 'Staff Tasks',   label: 'Staff Tasks',   Icon: Task,        Component: TasksScreen },
    { screen: 'Vendors',       label: 'Vendors',       Icon: Store,       Component: VendorsScreen },
    { screen: 'Inventory',     label: 'Inventory',     Icon: Boxes,       Component: InventoryScreen },
    { screen: 'Inspections',   label: 'Inspections',   Icon: Checklist,   Component: InspectionsScreen },
    { screen: 'Loading Dock',  label: 'Loading Dock',  Icon: Forklift,    Component: DeliveriesScreen },
    { screen: 'Cameras',       label: 'Cameras',       Icon: Videocam,    Component: CamerasScreen },
    { screen: 'Access Control', label: 'Access Control', Icon: Door,      Component: AccessControlScreen },
    { screen: 'Front Desk',    label: 'Front Desk',    Icon: Badge,       Component: VisitorsScreen },
    { screen: 'Keys & Access', label: 'Keys & Access', Icon: Key,         Component: KeysScreen },
    { screen: 'Parking',       label: 'Parking',       Icon: Parking,     Component: ParkingScreen },
    { screen: 'Incidents',     label: 'Incidents',     Icon: Report,      Component: IncidentsScreen },
    { screen: 'Emergency',     label: 'Emergency',     Icon: Megaphone,   Component: EmergencyScreen },
    { screen: 'Approvals',     label: 'Approvals',     Icon: Approval,    Component: ApprovalsScreen },
    { screen: 'Analytics',     label: 'Analytics',     Icon: Chart,       Component: AnalyticsScreen }
];

export const SCREENS_FOR = { resident: RESIDENT_SCREENS, staff: STAFF_SCREENS };

/* Where each side starts, and where tapping the open destination again
   returns to. Residents rest on the welcome screen; staff on the
   property dashboard. */
export const HOME_FOR = { resident: 'None', staff: 'Overview' };
